import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Nav, Tab, Modal, Form } from 'react-bootstrap';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom'; // Import useLocation and useNavigate
import { useMutation, useQueryClient } from 'react-query';
import { fetchTasks, deleteTask, Task, TaskFilters } from '../services/taskService';
import { useAuth } from '../contexts/AuthContext'; // Import useAuth
// Import sub-components
import TaskFilterBar from '../components/tasks/TaskFilterBar'; // Import the filter bar
import TaskCreateForm from '../components/tasks/TaskCreateForm'; // Import the create form
import TaskCard from '../components/tasks/TaskCard'; // Import the new TaskCard component
import ConfirmDeleteModal from '../components/common/ConfirmDeleteModal'; // Import the modal
import EditTaskModal from '../components/tasks/EditTaskModal'; // Import the Edit modal
import { sendConnectionRequest } from '../services/userService';
import { showToast } from '../components/common/ToastContainer';
// Import findOrCreateConversation
import { findOrCreateConversation } from '../services/messageService'; 

const TasksPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth(); // Get user and auth status
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation(); // Get location object
  const navigate = useNavigate(); // Initialize navigate
  const [initialExpandedTaskId, setInitialExpandedTaskId] = useState<number | null>(
    location.state?.expandTaskId || null
  );
  const queryClient = useQueryClient(); // Get query client

  // Filters state - remove status property if it exists
  const [filters, setFilters] = useState<Omit<TaskFilters, 'status'>>(() => {
    const initialFilters: Omit<TaskFilters, 'status'> = {}; 
    // Initialize other filters like search, distance from searchParams if needed
    return initialFilters;
  });

  // State for current view/tab
  const [activeView, setActiveView] = useState<string>(() => {
      const viewParam = searchParams.get('view') || 'all';
      // If trying to access my-tasks view but not authenticated, default to 'all'
      if (viewParam === 'my-tasks' && !isAuthenticated) {
          return 'all';
      }
      return viewParam;
  });

  // TODO: Add state for pagination
  // const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, hasMore: false });
  
  // State for create form visibility
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);

  // State for Delete Modal
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [taskToDeleteId, setTaskToDeleteId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false); // Loading state for delete

  // TODO: Add state for Edit/Delete Modals
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null); 

  // --- Connection Modal State (Copied from MapPage) ---
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [connectionRecipient, setConnectionRecipient] = useState<{ id: number; name: string }>({ id: 0, name: '' });
  const [connectionNote, setConnectionNote] = useState('');
  const [isSubmittingConnection, setIsSubmittingConnection] = useState(false);

  useEffect(() => {
    // If view is my-tasks and user is not authenticated, redirect view to all
    if (activeView === 'my-tasks' && !isAuthenticated) {
      setActiveView('all');
      setSearchParams({ view: 'all' }, { replace: true });
      return; // Stop fetching in this render cycle
    }

    const loadTasks = async () => {
      // Don't show main loading spinner if only filters change, 
      // but do show it on initial load or view change.
      // We might need a separate loading state for filter changes later.
      // For now, keep it simple.
      setIsLoading(true); 
      setError(null);
      try {
        // Ensure status is not included when fetching
        const currentFilters: TaskFilters = { 
            ...filters, // Spread filters which no longer contain status
            page: 1, 
            ...(activeView === 'tasks' && { type: 'TASK' }),
            ...(activeView === 'jobs' && { type: 'JOB' }),
            ...(activeView === 'my-tasks' && { ownerId: user?.id })
        };
        if (activeView === 'all' || activeView === 'my-tasks') {
             delete currentFilters.type; 
        }
        
        const response = await fetchTasks(currentFilters);
        setTasks(response.items);

      } catch (err: any) {
        setError(err.message || 'Failed to fetch tasks.');
      } finally {
        setIsLoading(false);
      }
    };

    loadTasks();
  }, [filters, activeView, user, isAuthenticated, setSearchParams]);

  // Effect to clear location state AND the initial expanded state
  useEffect(() => {
    // Clear the location state after initial read
    if (location.state?.expandTaskId || location.state?.conversationId) {
        window.history.replaceState({}, '');
    }
    // Clear the initial expanded task ID after the first render where it might be used
    if (initialExpandedTaskId !== null) {
        setInitialExpandedTaskId(null); 
    }
    // Run this effect only when location.state changes initially
  }, [location.state]); 

  // Handler for changing the active view/tab
  const handleViewChange = (view: string | null) => {
    if (view) {
       // Prevent switching to my-tasks if not authenticated
      if (view === 'my-tasks' && !isAuthenticated) {
          console.warn('Cannot switch to My Tasks/Jobs view when not logged in.');
          // Optionally show a message to the user here
          return; 
      }
      setActiveView(view);
      // Update URL search params to reflect the view
      setSearchParams({ view: view }, { replace: true });
    }
  };

  // Update handler to accept filters without status
  const handleFilterChange = (newFilters: Partial<Omit<TaskFilters, 'status'>>) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters,
    }));
    // TODO: Optionally update URL search params based on filters
  };

  // Handler to toggle the create form visibility
  const toggleCreateForm = () => {
    setShowCreateForm(!showCreateForm);
  };

  // Handler for when a task is successfully created
  const handleTaskCreated = (newTask: Task) => {
    // Add the new task to the beginning of the list for immediate feedback
    setTasks(prevTasks => [newTask, ...prevTasks]);
    // Hide the form
    setShowCreateForm(false);
    // Optionally, could refetch tasks if sorting/filtering needs to be reapplied
    // but adding to the top is often good UX for creation.
    // If the user created a task/job, switch to the 'my-tasks' view to see it
    if (isAuthenticated) {
        handleViewChange('my-tasks');
    }
  };

  // --- Placeholder Handlers for Task Card Actions --- 
  const handleEdit = (task: Task) => {
    console.log('Edit clicked for task:', task.id);
    setTaskToEdit(task);
    setShowEditModal(true);
  };

  // Updated handleDelete to show modal
  const handleDelete = (taskId: number) => {
    console.log('Delete requested for task:', taskId);
    setTaskToDeleteId(taskId);
    setShowDeleteModal(true);
  };

  const handleApply = (taskId: number) => {
    console.log('Apply clicked for task:', taskId);
    // TODO: Implement apply logic (e.g., API call, show confirmation)
    alert('Apply functionality not yet implemented.');
  };

  // Updated handleMessageOwner to replicate MapContainer logic
  const handleMessageOwner = async (ownerId: number, ownerName: string | null) => {
    console.log(`Message owner clicked: ${ownerName} (ID: ${ownerId})`);
    if (!isAuthenticated || !user) { // Check auth status
      showToast('Please log in to send messages.', 'danger');
      return;
    }
    
    showToast(`Starting conversation with ${ownerName || 'user'}...`, 'info');
    
    try {
      const conversationId = await findOrCreateConversation(ownerId);
      if (conversationId) {
        console.log('[TasksPage] Invalidating conversations query before navigation...');
        // Invalidate query to ensure list is fresh if new conversation created
        await queryClient.invalidateQueries(['conversations', user.id]); 
        // Navigate with the actual conversation ID
        navigate('/messages', { state: { conversationId } });
      } else {
        // Handle case where service returns null/undefined without throwing error
        showToast('Could not start or find conversation. Please try again.', 'danger');
      }
    } catch (error: any) {
      console.error('Error initiating message from TasksPage:', error);
      showToast(error.message || 'Failed to start conversation.', 'danger');
    }
  };

  // Updated handleConnectOwner to open the modal
  const handleConnectOwner = (ownerId: number, ownerName: string | null) => {
    console.log(`Connect owner clicked: ${ownerName} (ID: ${ownerId})`);
    if (ownerId && ownerName) {
      setConnectionRecipient({ id: ownerId, name: ownerName });
      setShowConnectionModal(true);
    } else {
      showToast('Cannot connect with this user (missing details).', 'warning');
    }
  };

  // --- Delete Modal Handlers ---
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setTaskToDeleteId(null);
    setIsDeleting(false); // Reset loading state if modal is closed before confirmation
  };

  const handleConfirmDelete = async () => {
    if (!taskToDeleteId) return;

    setIsDeleting(true);
    setError(null); // Clear previous errors

    try {
      await deleteTask(taskToDeleteId);
      // Remove the task from the state
      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskToDeleteId));
      handleCloseDeleteModal(); // Close modal on success
    } catch (err: any) {
      console.error('Failed to delete task:', err);
      // Display error to the user (e.g., using an Alert or a toast notification)
      // For simplicity, we'll set the main error state for now
      setError(err.message || 'Failed to delete the task.');
      // Optionally keep the modal open on error, or close it
      // handleCloseDeleteModal(); 
    } finally {
      setIsDeleting(false); // Ensure loading state is reset
    }
  };

  // --- Edit Modal Handlers ---
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setTaskToEdit(null);
  };

  // Callback from EditTaskModal on successful update
  const handleTaskUpdated = (updatedTask: Task) => {
    setTasks(prevTasks => 
      prevTasks.map(task => 
        task.id === updatedTask.id ? updatedTask : task
      )
    );
    // Modal is already hidden by itself on success, no need to call handleCloseEditModal explicitly
    // handleCloseEditModal(); 
  };

  // --- Connection Request Mutation (Copied from MapPage) --- //
  const { 
    mutate: submitConnectionRequestMutate, 
    isLoading: isSendingConnection // Renamed from MapPage's isLoading to avoid conflict
  } = useMutation( 
    (variables: { targetUserId: number, note?: string }) => 
      sendConnectionRequest(variables.targetUserId, variables.note),
    {
      onMutate: () => {
        setIsSubmittingConnection(true);
      },
      onSuccess: (data, variables) => {
        if (data.success) {
          setShowConnectionModal(false);
          setConnectionNote('');
          showToast(data.message || `Connection request sent to ${connectionRecipient.name}`, 'success');
          // Optionally invalidate queries related to connection status if needed here too
          // queryClient.invalidateQueries(['connectionStatus', variables.targetUserId]);
        } else {
          showToast(data.message || 'Failed to send connection request', 'danger');
        }
      },
      onError: (error: any) => {
        console.error('Error sending connection request mutation:', error);
        let errorMessage = 'Failed to send connection request. Please try again.';
        // Check if it's an Axios error and specifically a 409 Conflict
        if (error.isAxiosError && error.response?.status === 409) {
          // Use the specific message from the backend if available, otherwise use a default
          errorMessage = error.response?.data?.message || 'Connection request already sent or users already connected.';
          showToast(errorMessage, 'warning'); // Use warning severity for this specific case
        } else {
          // Fallback to generic error message for other errors
          errorMessage = error.message || errorMessage; // Use error.message if available
          showToast(errorMessage, 'danger');
        }
      },
      onSettled: () => {
        setIsSubmittingConnection(false);
      }
    }
  );

  // --- Submit Connection Request Form Handler (Copied from MapPage) --- //
  const submitConnectionRequest = () => {
    if (!connectionRecipient.id) return;
    submitConnectionRequestMutate({
      targetUserId: connectionRecipient.id,
      note: connectionNote,
    });
  };

  return (
    <Container className="py-5">
      {/* Page Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="mb-0">Task & Job Hub</h2>
            {/* Conditionally render create button only if logged in? */}
            {isAuthenticated && (
                <Button variant="primary" onClick={toggleCreateForm} aria-controls="create-task-form" aria-expanded={showCreateForm}> 
                <i className={`fas ${showCreateForm ? 'fa-times' : 'fa-plus'} me-1`}></i> {showCreateForm ? 'Cancel' : 'Post Task/Job'}
                </Button>
            )}
          </div>
        </Col>
      </Row>

      {/* Collapsible Create Task Form */} 
      {showCreateForm && isAuthenticated && (
          <div id="create-task-form">
              <TaskCreateForm 
                  onTaskCreated={handleTaskCreated} 
                  onCancel={toggleCreateForm} // Use toggle to hide form on cancel
              />
          </div>
      )}

      <Row>
        <Col>
          {/* Filter Bar */}
          <TaskFilterBar 
              initialFilters={filters} 
              onFilterChange={handleFilterChange} 
              activeView={activeView} 
          />

          <Card className="border-0 shadow-sm mb-4">
            <Card.Body>
              {/* Category/View Tabs */}
              <Nav variant="tabs" activeKey={activeView} onSelect={handleViewChange} className="mb-4">
                 <Nav.Item>
                   <Nav.Link eventKey="all">All</Nav.Link>
                 </Nav.Item>
                 <Nav.Item>
                   <Nav.Link eventKey="tasks">Tasks</Nav.Link>
                 </Nav.Item>
                 <Nav.Item>
                   <Nav.Link eventKey="jobs">Jobs</Nav.Link>
                 </Nav.Item>
                 {/* Conditionally render "My Tasks/Jobs" tab */}
                 {isAuthenticated && (
                    <Nav.Item>
                        <Nav.Link eventKey="my-tasks">My Tasks/Jobs</Nav.Link>
                    </Nav.Item>
                 )}
               </Nav>

               <p className="text-muted mb-0"> {/* Removed mb-3 */} 
                 Tasks are contract/freelance work while Jobs are formal employment through companies.
               </p>
               {/* Removed filter placeholders from here */}
            </Card.Body>
          </Card>

          {/* Task Grid Area */}
          {isLoading && (
            <div className="text-center py-5">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
            </div>
          )}
          {/* Add specific message if viewing my-tasks but not authenticated (shouldn't happen with checks) */}
          {activeView === 'my-tasks' && !isAuthenticated && (
             <Alert variant="warning">You must be logged in to view your tasks and jobs.</Alert>
          )}
          {error && !isLoading && (
            <Alert variant="danger">{error}</Alert>
          )}
          {!isLoading && !error && !(activeView === 'my-tasks' && !isAuthenticated) && (
            <Row xs={1} md={2} lg={3} className="g-4">
              {tasks.length === 0 ? (
                 <Col xs={12}>
                     <Alert variant="info">
                         {activeView === 'my-tasks' 
                           ? 'You have not posted any tasks or jobs yet.' 
                           : 'No tasks or jobs found matching your criteria.'}
                     </Alert>
                 </Col>
               ) : (
                 tasks.map(task => (
                   <Col key={task.id}>
                     <TaskCard 
                       task={task} 
                       onEdit={handleEdit}
                       onDelete={handleDelete}
                       onApply={handleApply}
                       onMessageOwner={handleMessageOwner}
                       onConnectOwner={handleConnectOwner}
                       startExpanded={task.id === initialExpandedTaskId}
                     />
                   </Col>
                 ))
              )}
            </Row>
          )}
          
          {/* TODO: Load More Button */} 
          {/* {pagination.hasMore && !isLoading && ( <Button onClick={handleLoadMore}>Load More</Button> )} */}

          {/* Render Delete Modal */} 
          <ConfirmDeleteModal 
            show={showDeleteModal}
            onHide={handleCloseDeleteModal}
            onConfirm={handleConfirmDelete}
            itemName="task/job" // Make the message more specific
            // You could add a loading state to the modal confirmation button
            // confirmDisabled={isDeleting} 
            // confirmLabel={isDeleting ? 'Deleting...' : 'Delete'}
          />

          {/* Render Edit Modal */} 
          <EditTaskModal 
             show={showEditModal}
             task={taskToEdit}
             onHide={handleCloseEditModal}
             onTaskUpdated={handleTaskUpdated}
          />

          {/* Render Connection Request Modal (Copied from MapPage) */}
          <Modal
            show={showConnectionModal}
            onHide={() => setShowConnectionModal(false)}
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Connect with {connectionRecipient.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <p>Add a personal note with your connection request:</p>
              <Form>
                <Form.Group>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Hi, I'd like to connect with you..."
                    value={connectionNote}
                    onChange={(e) => setConnectionNote(e.target.value)}
                  />
                </Form.Group>
              </Form>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowConnectionModal(false)}
                disabled={isSendingConnection} // Use the renamed isLoading variable
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={submitConnectionRequest}
                disabled={isSendingConnection} // Use the renamed isLoading variable
              >
                {isSendingConnection ? 'Sending...' : 'Send Request'}
              </Button>
            </Modal.Footer>
          </Modal>

        </Col>
      </Row>
    </Container>
  );
};

export default TasksPage;
